import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Shield, Loader2, Plus, X, Trash2, XCircle, Bot, Sparkles, ArrowDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { AIConversation } from '../../lib/ai';
import LoadingSpinner from '../LoadingSpinner';
import { motion, AnimatePresence } from 'framer-motion';

interface GuidedChatMessage {
  id: string;
  role: 'system' | 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface GuidedChat {
  id: string;
  title: string;
  summary: string | null;
  is_active: boolean;
  created_at: string;
}

export default function GuidedChat() {
  const { user } = useAuth();
  const [chats, setChats] = useState<GuidedChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<GuidedChat | null>(null);
  const [messages, setMessages] = useState<GuidedChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;

    const fetchChats = async () => {
      try {
        const { data, error } = await supabase
          .from('guided_chats')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setChats(data || []);
      } catch (err) {
        console.error('Error fetching chats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load chats');
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [user]);

  useEffect(() => {
    if (!selectedChat) {
      setMessages([]);
      return;
    }

    const fetchMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('guided_chat_messages')
          .select('*')
          .eq('chat_id', selectedChat.id)
          .order('created_at', { ascending: true });

        if (error) throw error;
        setMessages(data || []);
        scrollToBottom();
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError(err instanceof Error ? err.message : 'Failed to load messages');
      }
    };

    fetchMessages();
  }, [selectedChat]);

  // Handle scroll events to show/hide scroll button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sending && selectedChat?.is_active) {
        handleSend();
      }
    }
  };

  const createJournalEntry = async (chatId: string, summary: string) => {
    try {
      const { data: journalData, error: journalError } = await supabase
        .from('journal_entries')
        .insert([{
          user_id: user?.id,
          title: `Chat Summary: ${selectedChat?.title}`,
          content: summary,
          entry_type: 'chat_summary',
          chat_id: chatId,
          shadow_work_type: 'exploration',
          intensity_level: 3,
          tags: ['chat-summary'],
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (journalError) throw journalError;
      return journalData;
    } catch (err) {
      console.error('Error creating journal entry:', err);
      throw err;
    }
  };

  const generateChatSummary = async (chatId: string) => {
    try {
      const { data: chatMessages, error: messagesError } = await supabase
        .from('guided_chat_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      const summaryPrompt = `Please provide a concise summary of this therapy session, highlighting key insights, breakthroughs, and areas for further exploration. Focus on the main themes and emotional patterns discussed. Here are the messages:\n\n${chatMessages?.map(msg => `${msg.role}: ${msg.content}`).join('\n')}`;

      const aiResponse = await AIConversation.processMessage(summaryPrompt, user?.id || '');
      const summary = aiResponse.content;

      const { error: updateError } = await supabase
        .from('guided_chats')
        .update({ summary })
        .eq('id', chatId);

      if (updateError) throw updateError;

      await createJournalEntry(chatId, summary);

      return summary;
    } catch (err) {
      console.error('Error generating summary:', err);
      throw err;
    }
  };

  const handleEndChat = async () => {
    if (!selectedChat) return;

    try {
      setError(null);
      setSending(true);

      await generateChatSummary(selectedChat.id);

      const { error } = await supabase
        .from('guided_chats')
        .update({ is_active: false })
        .eq('id', selectedChat.id);

      if (error) throw error;

      setSelectedChat(prev => prev ? { ...prev, is_active: false } : null);
      setChats(current =>
        current.map(chat =>
          chat.id === selectedChat.id ? { ...chat, is_active: false } : chat
        )
      );
    } catch (err) {
      console.error('Error ending chat:', err);
      setError(err instanceof Error ? err.message : 'Failed to end chat');
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !user || !selectedChat || sending) return;

    setSending(true);
    setError(null);
    const currentInput = input;
    setInput('');

    try {
      const { data: userMsgData, error: userMsgError } = await supabase
        .from('guided_chat_messages')
        .insert([{
          chat_id: selectedChat.id,
          role: 'user',
          content: currentInput
        }])
        .select()
        .single();

      if (userMsgError) throw userMsgError;

      setMessages(current => [...current, userMsgData]);
      scrollToBottom();

      const aiResponse = await AIConversation.processMessage(currentInput, user.id);

      const { data: aiMsgData, error: aiMsgError } = await supabase
        .from('guided_chat_messages')
        .insert([{
          chat_id: selectedChat.id,
          role: 'assistant',
          content: aiResponse.content
        }])
        .select()
        .single();

      if (aiMsgError) throw aiMsgError;

      setMessages(current => [...current, aiMsgData]);
      scrollToBottom();

      inputRef.current?.focus();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      setInput(currentInput);
    } finally {
      setSending(false);
    }
  };

  const handleCreateChat = async (title: string) => {
    try {
      const { data, error } = await supabase
        .from('guided_chats')
        .insert([{
          user_id: user?.id,
          title,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;

      setChats([data, ...chats]);
      setSelectedChat(data);
      setShowNewChat(false);

      const { error: systemMsgError } = await supabase
        .from('guided_chat_messages')
        .insert([{
          chat_id: data.id,
          role: 'system',
          content: 'Welcome to your guided chat session. I\'m here to support your inner exploration with compassion and understanding. What would you like to focus on today?'
        }]);

      if (systemMsgError) throw systemMsgError;
    } catch (err) {
      console.error('Error creating chat:', err);
      setError(err instanceof Error ? err.message : 'Failed to create chat');
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    if (!confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('guided_chats')
        .delete()
        .eq('id', chatId);

      if (error) throw error;

      setChats(current => current.filter(chat => chat.id !== chatId));
      if (selectedChat?.id === chatId) {
        setSelectedChat(null);
      }
    } catch (err) {
      console.error('Error deleting chat:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete chat');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Chat Interface */}
        <div className="order-1 flex-1">
          {selectedChat ? (
            <div className="bg-white rounded-xl shadow-lg border border-mint-200 h-[calc(100vh-8rem)] flex flex-col">
              {/* Chat header */}
              <div className="p-4 border-b border-mint-200 bg-gradient-to-r from-midnight-50 to-mint-50 sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Bot className="h-6 w-6 text-amber-600" />
                    <div>
                      <h2 className="text-lg font-semibold text-midnight-800">
                        {selectedChat.title}
                      </h2>
                      <div className="flex items-center space-x-2 text-sm text-amber-600">
                        <Shield className="h-4 w-4" />
                        <span>Safe Space Active</span>
                      </div>
                    </div>
                  </div>
                  {selectedChat.is_active && (
                    <button
                      onClick={handleEndChat}
                      className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-bubblegum-50 text-bubblegum-600 hover:bg-bubblegum-100 transition-colors"
                      title="End chat session"
                    >
                      <XCircle className="h-4 w-4" />
                      <span>End Chat</span>
                    </button>
                  )}
                </div>
              </div>

              {error && (
                <div className="m-4 p-3 bg-bubblegum-50 text-bubblegum-700 rounded-lg text-sm border border-bubblegum-200">
                  {error}
                </div>
              )}

              {/* Messages area */}
              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth"
              >
                {messages.map(message => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`max-w-[80%] rounded-lg p-4 ${
                      message.role === 'user'
                        ? 'bg-mint-100 ml-auto'
                        : message.role === 'assistant'
                        ? 'bg-amber-50 border border-amber-200'
                        : 'bg-midnight-50 border border-midnight-200'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex items-center space-x-2 mb-2 text-amber-600">
                        <Bot className="h-4 w-4" />
                        <span className="text-sm font-medium">AI Therapist</span>
                      </div>
                    )}
                    <p className="text-midnight-800 whitespace-pre-wrap">{message.content}</p>
                    <span className="text-xs text-midnight-500 mt-2 block">
                      {new Date(message.created_at).toLocaleTimeString()}
                    </span>
                  </motion.div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Scroll to bottom button */}
              <AnimatePresence>
                {showScrollButton && (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    onClick={scrollToBottom}
                    className="absolute bottom-24 right-4 p-2 bg-amber-500 text-white rounded-full shadow-lg hover:bg-amber-600 transition-colors"
                  >
                    <ArrowDown className="h-5 w-5" />
                  </motion.button>
                )}
              </AnimatePresence>

              {/* Input area */}
              <div className="p-4 border-t border-mint-200 bg-gradient-to-r from-midnight-50 to-mint-50 sticky bottom-0">
                <div className="flex space-x-4">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={selectedChat.is_active ? "Share what's on your mind..." : "This chat has ended"}
                    disabled={sending || !selectedChat.is_active}
                    className="flex-1 px-4 py-3 bg-white border border-mint-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || !input.trim() || !selectedChat.is_active}
                    className="bg-gradient-to-r from-amber-500 to-marigold-500 text-white px-6 py-3 rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-sm"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        <span>Send</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg border border-mint-200 p-8 text-center">
              <Bot className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-midnight-800 mb-4">
                Welcome to Guided Chat
              </h3>
              <p className="text-midnight-600 mb-8 max-w-md mx-auto">
                Start a new conversation with your AI therapist for compassionate guidance and support in your inner work journey.
              </p>
              <button
                onClick={() => setShowNewChat(true)}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-marigold-500 text-white px-6 py-3 rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-sm"
              >
                <Plus className="h-5 w-5" />
                <span>Start New Chat</span>
              </button>
            </div>
          )}
        </div>

        {/* Chat List */}
        <div className="order-2 lg:w-80 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-lg border border-mint-200 p-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-midnight-800">Your Chats</h2>
              <button
                onClick={() => setShowNewChat(true)}
                className="p-2 hover:bg-mint-100 rounded-lg text-midnight-600 transition-colors"
                title="New Chat"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-2">
              {chats.map(chat => (
                <div
                  key={chat.id}
                  className={`group relative rounded-lg transition-all ${
                    selectedChat?.id === chat.id
                      ? 'bg-amber-50 border border-amber-200'
                      : 'hover:bg-mint-50 border border-transparent hover:border-mint-200'
                  }`}
                >
                  <button
                    onClick={() => setSelectedChat(chat)}
                    className="w-full text-left p-3"
                  >
                    <div className="font-medium truncate text-midnight-800">
                      {chat.title}
                    </div>
                    <div className="text-sm text-midnight-500 flex items-center space-x-2">
                      <span>{new Date(chat.created_at).toLocaleDateString()}</span>
                      {!chat.is_active && (
                        <span className="text-amber-600">(Ended)</span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChat(chat.id);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-midnight-400 hover:text-bubblegum-600 hover:bg-bubblegum-50 opacity-0 group-hover:opacity-100 transition-all"
                    title="Delete chat"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {chats.length === 0 && (
                <div className="text-center py-8 text-midnight-500">
                  <Sparkles className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                  <p>Start your first chat</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 bg-midnight-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-midnight-800">
                Start New Chat
              </h3>
              <button
                onClick={() => setShowNewChat(false)}
                className="text-midnight-400 hover:text-midnight-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const title = new FormData(e.currentTarget).get('title') as string;
                if (title.trim()) {
                  handleCreateChat(title);
                }
              }}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-midnight-700 mb-1"
                >
                  Chat Title
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  className="w-full px-4 py-2 border border-mint-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="e.g., Inner Child Work"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowNewChat(false)}
                  className="px-4 py-2 text-midnight-600 hover:text-midnight-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-marigold-500 text-white rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-sm"
                >
                  Create Chat
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}